<template>
    <div class="table-header pa-4">
        <div class="left-side">
            <div class="arrow-header pb-2">
                <span
                    v-if="tableCollapse"
                    :class="['arrow', { closed: !tableOpen }]"
                    @click="toggleTableOpen"
                />
                <h5
                    class="pr-3"
                    :class="['grey01--text', { 'pl-8': tableCollapse }]"
                    :data-cy="`table.title`"
                >
                    <slot name="tableHeaderName">
                        {{ tableName }}
                    </slot>
                </h5>
                <slot name="tableHeaderRight"></slot>
            </div>
            <p class="table-message small" :data-cy="`table.description`">
                <slot name="tableMessage"></slot>
            </p>
        </div>
        <div class="right-side">
            <div
                :class="[{ open: searchOpen && tableOpen }, 'search-holder']"
                :style="`width: ${searchWidth}`"
            >
                <div
                    @click="submitSearchValue"
                    :data-cy="`table.search.submit-search`"
                >
                    <font-awesome-icon
                        icon="fas fa-magnifying-glass"
                        class="icon teal-dark--text mr-3"
                    ></font-awesome-icon>
                </div>
                <AonDropdown
                    v-if="searchDetails.showDropdown"
                    class="mr-2"
                    :init-selection="searchableColumns[0]"
                    :items="searchableColumns"
                    width="50%"
                    @select-option="changeSearchableColumn"
                    :data-cy="`table.search.searchable-columns`"
                />
                <form @submit.prevent="submitSearchValue">
                    <AonInput
                        v-model="searchValue"
                        :placeholder="searchDetails.placeholder"
                        :data-cy="`table.search.search-input`"
                    />
                </form>
            </div>
            <div class="action-holder">
                <slot name="tableHeaderActionsPrepend"></slot>
                <div
                    v-if="toggleDensity"
                    v-tooltip="{
                        content: 'Toggle table row density',
                        delay: { show: 500, hide: 100 },
                    }"
                    class="dense-data-container mr-4"
                    :data-cy="`table.action.toggle-density`"
                >
                    <AonTabs
                        :tabs="denseTabs"
                        :current-tab="curTab"
                        button
                        icon-only
                        @tab-change="tabChange"
                    />
                </div>
                <template v-if="allowSearch">
                    <div
                        v-if="searchOpen && tableOpen"
                        :class="[{ disabled: !hasTableData }]"
                        @click="searchOpen = false"
                    >
                        <font-awesome-icon
                            icon="far fa-xmark"
                            :class="'icon icon-close ml-4 grey02--text'"
                            :data-cy="`table.search.close`"
                        ></font-awesome-icon>
                    </div>
                    <div
                        v-else
                        :class="[{ disabled: !hasTableData }]"
                        @click="
                            searchOpen = true;
                            setTableOpen();
                        "
                    >
                        <font-awesome-icon
                            icon="far fa-magnifying-glass"
                            :class="'icon ml-4 grey02--text'"
                            :data-cy="`table.search.open`"
                        ></font-awesome-icon>
                    </div>
                </template>
                <template v-if="hasFilters">
                    <div
                        :class="[
                            {
                                disabled: !hasTableData,
                                'ml-4': allowSearch,
                            },
                        ]"
                        @click="toggleFiltersOpen"
                        :data-cy="`table.action.toggle-filters`"
                    >
                        <font-awesome-icon
                            icon="far fa-filter"
                            :class="[
                                {
                                    'teal-dark--text': filtersOpen,
                                    'grey02--text': !filtersOpen,
                                },
                                'icon',
                            ]"
                        ></font-awesome-icon>
                    </div>
                </template>
                <template v-if="toggleColumns || tableActions.length > 0">
                    <AonDropdown
                        class="table-actions ml-1"
                        text-align="right"
                        :items="getDropdownItems"
                        icon-only-dropdown
                        :icon-only-dropdown-options="{
                            iconStyle: 'far',
                            iconColor: 'grey02',
                            iconColorActive: 'teal-dark',
                            iconName: 'fa-sliders-up',
                        }"
                        :data-cy="`table.action.toggle-actions`"
                        item-width="250px"
                        dropdown-pos="right"
                        @select-option="selectAction"
                    />
                </template>
                <slot name="tableHeaderActionsAppend"></slot>
            </div>
        </div>
        <slot></slot>
    </div>
    <TableBulkActionBar
        v-if="tableBulkActions.length > 0 && showBulkActions"
        class="mb-5"
        :bulk-actions="tableBulkActions"
        :rows-selected="selectedRows"
        @bulk-action="bulkAction"
    >
        <template #bulkActionBarPrepend>
            <slot name="bulkActionBarPrepend"></slot>
        </template>
    </TableBulkActionBar>
</template>

<script setup>
import { onBeforeMount, ref, computed } from 'vue';
import AonInput from '@moatmetrics/armory/src/components/base/forms/AonInput.vue';
import AonDropdown from '@moatmetrics/armory/src/components/base/forms/AonDropdown.vue';
import AonTabs from '@moatmetrics/armory/src/components/base/AonTabs.vue';
import TableBulkActionBar from './TableBulkActionBar.vue';

const emit = defineEmits([
    'column-toggled',
    'apply-search-filter',
    'toggle-filters-open',
    'toggle-table-open',
    'set-table-open',
    'action-selected',
    'dense-data-toggle',
    'bulk-action-selected'
]);

const props = defineProps({
    tableName: {
        type: String,
        default: null,
    },
    allowSearch: {
        type: Boolean,
        default: false,
    },
    columns: {
        type: Array,
        default: () => [],
    },
    tableOpen: {
        type: Boolean,
        default: true,
    },
    hasTableData: {
        type: Boolean,
        default: false,
    },
    tableCollapse: {
        type: Boolean,
        default: true,
    },
    toggleColumns: {
        type: Boolean,
        default: true,
    },
    tableActions: {
        type: Array,
        default: () => [],
    },
    tableBulkActions: {
        type: Array,
        default: () => [],
    },
    showBulkActions: {
        type: Boolean,
        default: false
    },
    toggleDensity: {
        type: Boolean,
        default: false,
    },
    denseData: {
        type: Boolean,
        default: true,
    },
    searchDetails: {
        type: Object,
        default: () => {
            return {
                showDropdown: true,
                placeholder: `Search`,
                filterType: 'text contains',
                filterColumn: null,
            };
        },
    },
    searchWidth: {
        type: String,
        default: '100px',
    },
    selectedRows: {
        type: Number
    }
});

const searchOpen = ref(false);
const searchValue = ref('');
const selectedColumn = ref(null);
const filtersOpen = ref(false);
const allCols = ref({ itemText: 'All Columns', itemValue: 'allColumns' });
const denseTabs = ref([
    {
        id: 1,
        name: 'dense',
        tabIcon: 'fa-minus',
    },
    {
        id: 2,
        name: 'expanded',
        tabIcon: 'fa-bars',
    },
]);
const curTab = ref(1);

const searchableColumns = computed(() => {
    return [
        allCols.value,
        ...props.columns
            .filter((val) => !val.hideFromFilters)
            .map((item) => ({
                itemText: item.locale,
                itemValue: item.value,
            })),
    ];
});

const searchDetail = computed(() => {
    return {
        filterColumn:
            props.searchDetails.filterColumn ?? selectedColumn.value.itemValue,
        filterValue: searchValue.value,
        filterType: props.searchDetails.filterType ?? 'text contains',
    };
});

const hasFilters = computed(() => {
    return props.columns.findIndex((val) => !val.hideFromFilters) > -1;
});

const getDropdownItems = computed(() => {
    let items = [];

    if (props.toggleColumns) {
        const subItems = props.columns
            .filter((val) => !val.headerCheckbox && !val.hideFromColToggle)
            .map((item) => ({
                itemText: item.locale,
                checked: item.showColumn,
            }));

        items.push({
            itemText: 'Show/Hide Columns',
            arrowSide: 'left',
            showSubItems: false,
            checkbox: true,
            items: subItems,
        });
    }
    items.push(...props.tableActions);
    return items;
});

// NEEDED??????? -RR
onBeforeMount(() => {
    selectedColumn.value = allCols.value;
});

const changeSearchableColumn = (selectedItem) => {
    selectedColumn.value = selectedItem;
};

const submitSearchValue = () => {
    emit('apply-search-filter', searchDetail.value);
};

const toggleFiltersOpen = () => {
    filtersOpen.value = !filtersOpen.value;
    emit('toggle-filters-open', filtersOpen.value);
    setTableOpen();
};

const toggleTableOpen = () => {
    emit('toggle-table-open');
};
const setTableOpen = () => {
    emit('set-table-open');
};

const selectAction = (action) => {
    if (action.parent === 'Show/Hide Columns') {
        emit('column-toggled', action);
    } else {
        emit('action-selected', action.itemText);
    }
};

const bulkAction = (action) => {
    emit('bulk-action-selected', action);
};

const tabChange = (tab) => {
    if (tab.id !== curTab.value) {
        curTab.value = tab.id;
        emit('dense-data-toggle');
    }
};
</script>

<script>
export default {
    name: 'TableHeader',
    components: {
        AonInput,
        AonDropdown,
        AonTabs,
    },
};
</script>

<style lang="scss" scoped>
.table-header {
    width: 100%;
    padding: 10px 20px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    position: relative;
    border-bottom: none;
    border-radius: inherit;
    border-bottom-left-radius: unset;
    border-bottom-right-radius: unset;

    .arrow-header {
        display: flex;
        width: 100%;
    }

    .arrow {
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 9px solid #46535e;
        border-radius: 2px;
        position: absolute;
        margin-top: 8px;
        margin-left: 5px;

        transition: all 0.3s ease-in-out;

        &.closed {
            transform: rotate(-90deg);
        }

        &:hover {
            cursor: pointer;
            color: $tealDarkHover;
        }
    }

    .table-message {
        font-family: $font-family-heading;
    }

    .icon {
        cursor: pointer;

        &.icon-close {
            width: 24px;
            height: 24px;
        }
    }

    .right-side,
    .left-side {
        width: 50%;
        display: flex;
        align-items: center;
    }

    .left-side {
        flex-wrap: wrap;
    }

    .right-side {
        justify-content: flex-end;
    }

    .search-holder,
    .action-holder {
        display: flex;
        align-items: center;
    }

    .search-holder {
        width: 100%;
        position: relative;
        z-index: 1;

        opacity: 0;
        transform: translateY(-50px);
        pointer-events: none;

        transition: all 0.3s ease-in-out;

        &.open {
            opacity: 1;
            transform: translateY(0);
            pointer-events: all;
        }
    }

    .action-holder {
        justify-content: flex-end;
    }
}
</style>
